import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { CheckCircle, Cancel, People } from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosConfig';
import RejectRequestModal from './components/RejectRequestModal';
import CoverageModal from './components/CoverageModal';

interface LeaveRequest {
  _id: string;
  personnelId: {
    _id: string;
    name: string;
    email: string;
    leaveEntitlement?: {
      annualLeaveTotal: number;
      annualLeaveUsed: number;
      timeInLieuHours: number;
      timeInLieuUsed: number;
    };
    currentLeaveBalance?: {
      annualLeave: number;
      timeInLieu: number;
    };
  };
  requestedBy: {
    _id: string;
    name: string;
  };
  type: 'annual-leave' | 'time-lieu';  // ✅ FIXED: was leaveType
  dateStart: string;                    // ✅ FIXED: was startDate
  dateEnd?: string;
  hoursRequested?: number;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;                    // ✅ FIXED: was requestedAt
  decisionBy?: {
    _id: string;
    name: string;
  };
  decisionDate?: string;
}

interface CoverageData {
  date: string;
  working: string[];     // Array of staff names
  onLeave: string[];
  unavailable: string[];
  notScheduled: string[];
  totalStaff: number;
}

const PendingRequestsTab: React.FC = () => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [coverage, setCoverage] = useState<Map<string, CoverageData>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [coverageModalOpen, setCoverageModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [requestToApprove, setRequestToApprove] = useState<LeaveRequest | null>(null);


  useEffect(() => {
    fetchRequests();
  }, []);

const fetchRequests = async () => {
  try {
    setLoading(true);
    setError(null);
    
    console.log('📋 Fetching leave requests...');
    const response = await axiosInstance.get('/api/leave-requests');
    const allRequests = response.data;
    console.log('✅ Got requests:', allRequests.length);
    
    // Filter: ONLY pending requests (remove approved future requests)
    const pendingOnly = allRequests.filter((req: LeaveRequest) => 
      req.status === 'pending'
    );
    
    console.log('✅ Pending requests:', pendingOnly.length);
    setRequests(pendingOnly);
    
    // Fetch coverage for all unique dates
    const uniqueDates = [...new Set(pendingOnly.map((req: LeaveRequest) => req.dateStart))] as string[];
    if (uniqueDates.length > 0) {
      await fetchCoverageForDates(uniqueDates);
    }
    
    setLoading(false);
  } catch (err: any) {
    console.error('❌ Error fetching requests:', err);
    setError(err.response?.data?.message || 'Failed to fetch leave requests');
    setLoading(false);
  }
};


  const fetchCoverageForDates = async (dates: string[]) => {
    const coverageMap = new Map<string, CoverageData>();
    
    for (const date of dates) {
      try {
        // ✅ FIXED: Changed endpoint from /coverage?date= to /coverage/:date
        const dateStr = new Date(date).toISOString().split('T')[0];
        console.log(`🔍 Fetching coverage for ${dateStr}`);
        const response = await axiosInstance.get(`/api/rota/coverage/${dateStr}`);
        coverageMap.set(date, response.data);
      } catch (err: any) {
        console.error(`❌ Failed to fetch coverage for ${date}:`, err.response?.status, err.response?.data);
        // Don't fail the whole thing if coverage fails
      }
    }
    
    setCoverage(coverageMap);
  };

const handleApproveClick = (request: LeaveRequest) => {
  setRequestToApprove(request);
  setApproveModalOpen(true);
};

const handleApproveConfirm = async () => {
  if (!requestToApprove) return;
  
  try {
    console.log('✅ Approving request:', requestToApprove._id);
    await axiosInstance.patch(`/api/leave-requests/${requestToApprove._id}/approve`);
    
    setSuccessMessage(`Approved ${requestToApprove.hoursRequested || 1} ${requestToApprove.type === 'time-lieu' ? 'hours TIL' : 'day(s) AL'} for ${requestToApprove.personnelId.name}`);
    setTimeout(() => setSuccessMessage(null), 5000);
    
    setApproveModalOpen(false);
    setRequestToApprove(null);
    fetchRequests();
  } catch (err: any) {
    console.error('❌ Error approving:', err);
    setError(err.response?.data?.message || 'Failed to approve request');
    setTimeout(() => setError(null), 5000);
  }
};


  const handleRejectClick = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setRejectModalOpen(true);
  };

const handleRejectConfirm = async (reason: string) => {
  if (!selectedRequest) return;
  
  try {
    console.log('❌ Rejecting request:', selectedRequest._id);
    await axiosInstance.patch(`/api/leave-requests/${selectedRequest._id}/reject`, {
      decisionNote: reason
    });
    
    // Close modal first
    setRejectModalOpen(false);
    setSelectedRequest(null);
    
    // Show prominent success message
    setSuccessMessage(`✅ Rejected ${selectedRequest.type === 'time-lieu' ? 'TIL' : 'AL'} request from ${selectedRequest.personnelId.name}. Staff member has been notified.`);
    setTimeout(() => setSuccessMessage(null), 8000); // Show for 8 seconds
    
    // Refresh list
    fetchRequests();
  } catch (err: any) {
    console.error('❌ Error rejecting:', err);
    setError(err.response?.data?.message || 'Failed to reject request');
    setTimeout(() => setError(null), 5000);
  }
};


  const handleCoverageClick = (date: string) => {
    setSelectedDate(date);
    setCoverageModalOpen(true);
  };

  const calculateNoticeDays = (createdAt: string, dateStart: string): number => {  // ✅ FIXED param names
    try {
      const requested = new Date(createdAt);
      const start = new Date(dateStart);
      
      if (isNaN(requested.getTime()) || isNaN(start.getTime())) {
        return 0;
      }
      
      const diffTime = start.getTime() - requested.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (e) {
      return 0;
    }
  };

  const getNoticePeriodChip = (days: number) => {
    if (days < 7) {
      return <Chip label={`${days}d notice`} size="small" color="error" />;
    } else if (days < 14) {
      return <Chip label={`${days}d notice`} size="small" color="warning" />;
    } else {
      return <Chip label={`${days}d notice`} size="small" color="success" />;
    }
  };

// Replace the getCoverageChip function with this:

const getCoverageChip = (date: string) => {
  const coverageData = coverage.get(date);
  
  if (!coverageData) {
    return <Chip label="Loading..." size="small" />;
  }
  
  const working = coverageData.working?.length || 0;
  const total = coverageData.totalStaff || 0;
  const notScheduled = coverageData.notScheduled?.length || 0;
  const scheduled = total - notScheduled;
  
  // If more than 50% not scheduled, show grey "Not scheduled" chip
  if (notScheduled > total * 0.5) {
    return (
      <Chip
        icon={<People />}
        label={`${scheduled}/${total} scheduled`}
        size="small"
        sx={{ bgcolor: '#9E9E9E', color: 'white' }}
        onClick={() => handleCoverageClick(date)}
      />
    );
  }
  
  // Normal coverage logic
  return (
    <Chip
      icon={<People />}
      label={`${working}/${total} staff`}
      size="small"
      color={working >= 3 ? 'success' : working >= 2 ? 'warning' : 'error'}
      onClick={() => handleCoverageClick(date)}
      sx={{ cursor: 'pointer' }}
    />
  );
};

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
    {error && (
  <Alert 
    severity="error" 
    sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }} 
    onClose={() => setError(null)}
  >
    {error}
  </Alert>
)}

{successMessage && (
  <Alert 
    severity="success" 
    sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }} 
    onClose={() => setSuccessMessage(null)}
  >
    {successMessage}
  </Alert>
)}

      {requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No pending or future approved requests
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Balance</TableCell>  {/* ✅ NEW COLUMN */}
                <TableCell>Notice</TableCell>
                <TableCell>Coverage</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => {
                const noticeDays = calculateNoticeDays(request.createdAt, request.dateStart);  // ✅ FIXED field names
                const balance = request.personnelId.currentLeaveBalance;
                
                return (
                  <TableRow key={request._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.personnelId.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.type === 'annual-leave' ? 'AL' : 'TIL'}  // ✅ FIXED field name
                        size="small"
                        color={request.type === 'annual-leave' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(request.dateStart)}  {/* ✅ FIXED field name */}
                    </TableCell>
                    <TableCell>
                      {request.hoursRequested ? `${request.hoursRequested}h` : '1 day'}
                    </TableCell>
                    <TableCell>  {/* ✅ NEW: Show balance */}
                      {balance ? (
                        <Stack spacing={0.5}>
                          {request.type === 'annual-leave' ? (
                            <Typography variant="caption" color={balance.annualLeave < 5 ? 'error.main' : 'success.main'}>
                              AL: {balance.annualLeave}d left
                            </Typography>
                          ) : (
                            <Typography variant="caption" color={balance.timeInLieu < 8 ? 'warning.main' : 'success.main'}>
                              TIL: {balance.timeInLieu}h left
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{getNoticePeriodChip(noticeDays)}</TableCell>
                    <TableCell>{getCoverageChip(request.dateStart)}</TableCell>  {/* ✅ FIXED field name */}
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {request.reason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status}
                        size="small"
                        color={request.status === 'approved' ? 'success' : request.status === 'pending' ? 'warning' : 'default'}
                      />
                    </TableCell>
                  <TableCell align="center">
  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
    <Tooltip title="Approve">
      <IconButton
        size="small"
        color="success"
        onClick={() => handleApproveClick(request)}
      >
        <CheckCircle />
      </IconButton>
    </Tooltip>
    <Tooltip title="Reject">
      <IconButton
        size="small"
        color="error"
        onClick={() => handleRejectClick(request)}
      >
        <Cancel />
      </IconButton>
    </Tooltip>
  </Box>
</TableCell>

                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RejectRequestModal
        open={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedRequest(null);
        }}
        onConfirm={handleRejectConfirm}
      />

      <CoverageModal
        open={coverageModalOpen}
        date={selectedDate}
        onClose={() => {
          setCoverageModalOpen(false);
          setSelectedDate(null);
        }}
      />
      <Dialog
  open={approveModalOpen}
  onClose={() => {
    setApproveModalOpen(false);
    setRequestToApprove(null);
  }}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <CheckCircle color="success" />
    Confirm Approval
  </DialogTitle>
  <DialogContent>
    {requestToApprove && (
      <Box>
        <Typography variant="body1" gutterBottom>
          Approve this leave request?
        </Typography>
        
        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 1,
          mt: 2
        }}>
          <Typography variant="body2" gutterBottom>
            <strong>Staff:</strong> {requestToApprove.personnelId.name}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Type:</strong> {requestToApprove.type === 'annual-leave' ? 'Annual Leave' : 'Time in Lieu'}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Date:</strong> {formatDate(requestToApprove.dateStart)}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Amount:</strong> {requestToApprove.hoursRequested ? `${requestToApprove.hoursRequested} hours` : '1 day'}
          </Typography>
          {requestToApprove.reason && (
            <Typography variant="body2">
              <strong>Reason:</strong> {requestToApprove.reason}
            </Typography>
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            This will:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Deduct from staff member's balance</li>
              <li>Add entry to rota calendar</li>
              <li>Send approval email to staff</li>
              <li>Log transaction in audit trail</li>
            </ul>
          </Typography>
        </Alert>
      </Box>
    )}
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => {
        setApproveModalOpen(false);
        setRequestToApprove(null);
      }}
    >
      Cancel
    </Button>
    <Button 
      onClick={handleApproveConfirm}
      variant="contained"
      color="success"
      startIcon={<CheckCircle />}
    >
      Approve
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default PendingRequestsTab;